'use client'

import * as React from 'react'

/** Pages loaded in a row while the user has not scrolled, before "Show older" takes over. */
export const LOAD_MORE_AUTO_LIMIT = 10

export interface UseLoadMoreOptions {
  hasMore: boolean
  onLoadMore?: () => void | Promise<void>
  /** Start loading this far before the end of the list comes into view. */
  rootMargin?: string
  /**
   * Pages fetched back to back while the end stays in view without the user
   * scrolling. A list that folds many rows into one line (the bell's Following
   * group) can stay short for several pages; this stops it walking the whole
   * inbox on its own. Scrolling resets it.
   */
  maxAutoLoads?: number
  /** A change (a tab switch) resets the auto-load budget and any failure. */
  resetKey?: unknown
}

export interface UseLoadMoreResult {
  /** The list's scroll container. Leave it unattached to watch the viewport. */
  rootRef: (element: HTMLElement | null) => void
  /** An element at the end of the list. */
  sentinelRef: (element: HTMLElement | null) => void
  loadingMore: boolean
  /** A click or a key press: loads the next page and resets the auto-load budget. */
  loadMore: () => Promise<void>
  /**
   * False when the next page will not load on its own: no IntersectionObserver,
   * the auto-load budget is spent, or the last page failed. Show "Show older" then.
   */
  autoLoading: boolean
}

/**
 * Infinite scroll for the notification lists: the next page loads when the
 * sentinel at the end of the list comes near the visible area of its scroll
 * container, and keeps loading while the list still does not fill it. One
 * page at a time; `onLoadMore` is never called again while a call is pending.
 */
export function useLoadMore({
  hasMore,
  onLoadMore,
  rootMargin = '0px 0px 320px 0px',
  maxAutoLoads = LOAD_MORE_AUTO_LIMIT,
  resetKey,
}: UseLoadMoreOptions): UseLoadMoreResult {
  const [root, setRoot] = React.useState<HTMLElement | null>(null)
  const [sentinel, setSentinel] = React.useState<HTMLElement | null>(null)
  const [loadingMore, setLoadingMore] = React.useState(false)
  // Bumped after every page lands so a fresh observer re-checks the sentinel:
  // an observer only reports changes, and a still-visible end is not a change.
  const [generation, setGeneration] = React.useState(0)
  const [paused, setPaused] = React.useState(false)
  const [failed, setFailed] = React.useState(false)
  // Assume an observer until mounted, so server and client render the same.
  const [observable, setObservable] = React.useState(true)
  const inFlight = React.useRef(false)
  const autoLoads = React.useRef(0)
  const latest = React.useRef({ hasMore, onLoadMore })
  latest.current = { hasMore, onLoadMore }

  const run = React.useCallback(async () => {
    const { hasMore: more, onLoadMore: load } = latest.current
    if (!load || !more || inFlight.current) {
      return false
    }
    inFlight.current = true
    setLoadingMore(true)
    try {
      await load()
      return true
    } catch {
      setFailed(true)
      return false
    } finally {
      inFlight.current = false
      setLoadingMore(false)
      setGeneration((count) => count + 1)
    }
  }, [])

  const loadMore = React.useCallback(async () => {
    autoLoads.current = 0
    setPaused(false)
    setFailed(false)
    await run()
  }, [run])

  React.useEffect(() => {
    autoLoads.current = 0
    setPaused(false)
    setFailed(false)
  }, [resetKey])

  React.useEffect(() => {
    setObservable(typeof IntersectionObserver !== 'undefined')
  }, [])

  // The user scrolling is what spends the budget back.
  React.useEffect(() => {
    const target: HTMLElement | Window | null = root ?? (typeof window === 'undefined' ? null : window)
    if (!target) {
      return
    }
    const onScroll = () => {
      autoLoads.current = 0
      setPaused(false)
    }
    target.addEventListener('scroll', onScroll, { passive: true })
    return () => target.removeEventListener('scroll', onScroll)
  }, [root])

  React.useEffect(() => {
    if (!sentinel || !hasMore || paused || failed || typeof IntersectionObserver === 'undefined') {
      return
    }
    const observer = new IntersectionObserver(
      (records) => {
        if (!records.some((record) => record.isIntersecting) || inFlight.current) {
          return
        }
        if (autoLoads.current >= maxAutoLoads) {
          setPaused(true)
          return
        }
        autoLoads.current += 1
        void run()
      },
      { root, rootMargin },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [sentinel, root, rootMargin, hasMore, paused, failed, generation, maxAutoLoads, run])

  return {
    rootRef: setRoot,
    sentinelRef: setSentinel,
    loadingMore,
    loadMore,
    autoLoading: observable && !paused && !failed,
  }
}
